import React from "react";

import { IClass, IStudent, ISubClass } from "../../../state";
import Dropdown, { IOption } from "../../../atoms/Dropdown";
import { EStudentSortType, SortStudents } from "../../../libs/Util";

import styles from "./EditSubClassStudentForm.module.scss";

export interface EditSubClassStudentFormProps {
  title: string;
  calling: boolean;
  students: IStudent[];
  student_ids: number[];
  classes: IClass[];
  sub_class: ISubClass;
  updateSubClassStudents: (student_ids: number[]) => void;
}

const EditSubClassStudentForm = (props: EditSubClassStudentFormProps) => {
  const [selectedIds, setSslectedIds] = React.useState<Set<number>>(
    new Set(props.student_ids),
  );

  const [searchName, setSearchName] = React.useState("");
  const [searchStudentNo, setSearchStudentNo] = React.useState("");
  const [searchClasses, setSearchClasses] = React.useState<IOption[]>([]);
  const [sortBy, setSortBy] = React.useState<EStudentSortType>(
    EStudentSortType.id,
  );

  React.useEffect(() => {
    setSslectedIds(new Set(props.student_ids));
  }, [props.student_ids]);

  const classOptions = React.useMemo(
    () => props.classes.map(cls => ({ value: cls.id, label: cls.full_name })),
    [props.classes],
  );

  const filteredStudents = React.useMemo(() => {
    let students = [...props.students];
    if (searchName) {
      const lowercasedSearch = searchName.toLowerCase();
      students = students.filter(t => {
        const fullName = `${t.last_name} ${t.first_name}`;
        return fullName.toLowerCase().includes(lowercasedSearch);
      });
    }
    if (searchStudentNo) {
      students = students.filter(s => {
        return s.student_no_in_school?.includes(searchStudentNo);
      });
    }
    if (searchClasses.length) {
      students = students.filter(s => {
        return searchClasses.some(cls => cls.value === s.class_id);
      });
    }
    SortStudents(students, sortBy);
    return students;
  }, [
    props.students,
    searchName,
    searchStudentNo,
    searchClasses,
    sortBy,
  ]);

  const bulkChecked = React.useMemo(() => {
    if (filteredStudents.length === 0) return false;
    return filteredStudents.every(s => selectedIds.has(s.id));
  }, [filteredStudents, selectedIds]);

  const subClassName = React.useMemo(
    () => (props.sub_class ? props.sub_class.name : ""),
    [props.sub_class],
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>{props.title}</div>
      <div className={styles.label + " mb-3"}>●グループ名</div>
      <div className={styles.group}>{subClassName}</div>
      <div className={styles.searchArea}>
        <div className={styles.label}>
          ●グループに所属する生徒を選択してください
        </div>
        <div className="d-flex align-items-center">
          <label className="ms-2 me-2">並び順</label>
          <select
            name="sortBy"
            className="form-select"
            aria-label="select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value as EStudentSortType)}
          >
            <option value={EStudentSortType.id}>ID</option>
            <option value={EStudentSortType.student_no_in_school}>
              学籍番号
            </option>
            <option value={EStudentSortType.full_name_kana}>50音順</option>
          </select>
        </div>
        <div className={styles.searchRow}>
          <div className={styles.input}>
            <label className="me-2">名前検索</label>
            <input
              value={searchName}
              className="form-control"
              placeholder="氏名を入力"
              onChange={e => {
                setSearchName(e.target.value);
              }}
            />
          </div>
          <div className={styles.input}>
            <label className="ms-2 me-2">学籍番号検索</label>
            <input
              value={searchStudentNo}
              className="form-control"
              placeholder="学籍番号を入力"
              onChange={e => setSearchStudentNo(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center">
            <label className="ms-2 me-2">クラス検索</label>
            <Dropdown
              className={styles.dropdown}
              placeholder="クラスを選択"
              options={classOptions}
              onChange={value => setSearchClasses(value)}
              multiple
            />
          </div>
        </div>
      </div>
      <div className={styles.table}>
        <table className="table table-striped table-hover mb-0">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={bulkChecked}
                  disabled={filteredStudents.length === 0}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { checked } = e.currentTarget;
                    setSslectedIds(prevState => {
                      const newState = new Set(prevState);
                      if (checked) {
                        filteredStudents.forEach(s => newState.add(s.id));
                      } else {
                        filteredStudents.forEach(s => newState.delete(s.id));
                      }
                      return newState;
                    });
                  }}
                />
              </th>
              <th>ID</th>
              <th>学籍番号</th>
              <th>名前</th>
              <th>クラス</th>
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {filteredStudents.map(s => {
              return (
                <tr key={s.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      value={s.id}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const { checked } = e.currentTarget;
                        setSslectedIds(prevState => {
                          const newState = new Set(prevState);
                          if (checked) {
                            newState.add(s.id);
                          } else {
                            newState.delete(s.id);
                          }
                          return newState;
                        });
                      }}
                    />
                  </td>
                  <td>{s.id}</td>
                  <td>{s.student_no_in_school}</td>
                  <td>{`${s.last_name} ${s.first_name}`}</td>
                  <td>
                    {props.classes
                      .filter(cls => {
                        return cls.id === s.class_id;
                      })
                      .map(c => {
                        return <div key={`${c.id}`}>{c.full_name}</div>;
                      })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button
        className="btn btn-primary"
        disabled={props.calling}
        onClick={() => {
          props.updateSubClassStudents(Array.from(selectedIds));
        }}
      >
        登録
      </button>
    </div>
  );
};

export default EditSubClassStudentForm;
